import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { HiOutlineBuildingLibrary } from "react-icons/hi2";

import type { AdminPayoutTransaction } from "../../lib/api/adminTransactionsData";
import { formatAmount } from "../../utils/FormatPerson";
import { statusBadgeClass } from "../orders/ordersOrderTable";
import {
  getPayoutBankDisplayName,
  getPayoutDisplayDate,
} from "./payoutsTransactionsTable";

type Props = {
  open: boolean;
  onClose: () => void;
  transaction: AdminPayoutTransaction | null;
  banksByCode: Map<string, string>;
};

function str(v: unknown): string {
  return typeof v === "string" ? v : "";
}

function num(v: unknown): number | undefined {
  if (typeof v === "number" && !Number.isNaN(v)) return v;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isNaN(n) ? undefined : n;
  }
  return undefined;
}

function SectionCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-[#2C363E] bg-[#161C22]/55 p-4 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.035)] sm:p-5">
      <h3 className="mb-4 flex items-center gap-2.5 text-[11px] font-bold uppercase tracking-[0.18em] text-[#869AA9]">
        <span
          className="h-px w-7 shrink-0 bg-gradient-to-r from-[#4FB0FF]/90 to-transparent"
          aria-hidden
        />
        {title}
      </h3>
      <div className="divide-y divide-[#23292F]">{children}</div>
    </section>
  );
}

function KV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1 py-3.5 first:pt-0 last:pb-0 sm:flex-row sm:justify-between sm:gap-6 sm:items-start">
      <dt className="text-[12px] font-medium text-[#7D8F9C] shrink-0 min-w-[7.5rem]">
        {label}
      </dt>
      <dd className="min-w-0 text-[13px] font-medium leading-snug text-[#EEF2F6] text-left sm:text-right break-words">
        {value}
      </dd>
    </div>
  );
}

function CopyChip({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);

  const onCopy = async () => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Could not copy");
    }
  };

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        void onCopy();
      }}
      className="inline-flex max-w-full items-center gap-2 rounded-lg border border-[#3A4852]/80 bg-[#0D1114]/80 px-2.5 py-1.5 text-left transition hover:border-[#4FB0FF]/40 hover:bg-[#1C2227]"
    >
      <span className="min-w-0 truncate font-mono text-[11px] leading-snug text-[#C6D7E4]">
        {text}
      </span>
      <span className="shrink-0 rounded border border-[#3A4852] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-[#AEBCC6]">
        {copied ? "Done" : "Copy"}
      </span>
    </button>
  );
}

const AdminPayoutDetailModal = ({
  open,
  onClose,
  transaction,
  banksByCode,
}: Props) => {
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => closeRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !transaction) return null;

  const tx = transaction;
  const u = tx.user;
  let displayName = "—";
  let email = "";
  if (u && typeof u === "object") {
    const profile = (
      u as { profile?: { firstName?: string; lastName?: string } }
    ).profile;
    const f = profile?.firstName?.trim() ?? "";
    const l = profile?.lastName?.trim() ?? "";
    displayName = `${f} ${l}`.trim() || "—";
    email =
      (u as { email?: { address?: string } }).email?.address?.trim() ?? "";
  }

  const rec =
    tx.recipient &&
    typeof tx.recipient === "object" &&
    !Array.isArray(tx.recipient)
      ? (tx.recipient as Record<string, unknown>)
      : null;

  const asset =
    tx.asset && typeof tx.asset === "object"
      ? (tx.asset as Record<string, unknown>)
      : null;

  const usd = num(asset?.amountInUSD);
  const nativeAmt = num(asset?.amount);
  const ngn = num(tx.payoutAmount);
  const assetName = str(asset?.name) || "—";

  const statusLabel = str(tx.status) || "—";
  const providerLabel = str(tx.provider) || "—";
  const idShort =
    tx._id.length > 14 ? `${tx._id.slice(0, 8)}…${tx._id.slice(-4)}` : tx._id;

  const bankDisplay = getPayoutBankDisplayName(tx, banksByCode);
  const headlineDate = getPayoutDisplayDate(tx);

  const linked =
    tx.transaction &&
    typeof tx.transaction === "object" &&
    !Array.isArray(tx.transaction)
      ? (tx.transaction as { type?: unknown; id?: unknown })
      : null;

  return (
    <div
      className="fixed inset-0 z-[150] flex items-end justify-center p-0 sm:items-center sm:p-6 font-manrope"
      role="presentation"
    >
      <button
        type="button"
        aria-label="Close dialog"
        className="absolute inset-0 bg-black/70 backdrop-blur-[2px]"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="payout-detail-title"
        className="relative z-[151] flex min-w-0 max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-t-2xl border border-[#2C363E] bg-[#13181B] shadow-[0_-12px_48px_-16px_rgba(0,0,0,0.88)] sm:max-h-[88vh] sm:rounded-2xl sm:shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="shrink-0 border-b border-[#2C363E]/90 bg-gradient-to-br from-[#1a222d] via-[#141a20] to-[#13181B] px-4 pb-5 pt-4 sm:px-6 sm:pb-6 sm:pt-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1 space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`inline-flex rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${statusBadgeClass(
                    tx.status
                  )}`}
                >
                  {statusLabel}
                </span>
                {providerLabel !== "—" ? (
                  <span className="inline-flex rounded-full border border-[#3A4852] bg-[#1C2227]/90 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-[#AEBCC6]">
                    {providerLabel}
                  </span>
                ) : null}
                {tx.isAutomated === true ? (
                  <span className="inline-flex rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-emerald-300/95">
                    Automated
                  </span>
                ) : null}
              </div>
              <h2
                id="payout-detail-title"
                className="font-sora text-lg font-bold leading-snug text-white sm:text-xl"
              >
                <span>Payout</span>
                <span className="font-normal text-[#5C6B76]"> · </span>
                <span className="font-mono text-[15px] font-semibold tracking-tight text-[#C6D7E4] sm:text-[16px]">
                  {idShort}
                </span>
              </h2>
              <p className="text-[13px] font-medium leading-relaxed text-[#B8C5CE]">
                <span className="font-semibold text-[#BEE1FB]">
                  {assetName}
                </span>
                {nativeAmt != null ? (
                  <>
                    {" "}
                    <span className="text-[#5C6B76]">·</span>{" "}
                    {formatAmount(nativeAmt)} native
                  </>
                ) : null}
                {usd != null ? (
                  <>
                    {" "}
                    <span className="text-[#5C6B76]">·</span> $
                    {formatAmount(usd)} USD
                  </>
                ) : null}
              </p>
              <p className="text-[11px] text-[#617889]">
                Created ·{" "}
                <span className="font-medium text-[#8FA3AF]">
                  {headlineDate}
                </span>
              </p>
              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-[#617889]">
                  Full ID
                </span>
                <CopyChip text={tx._id} label="Payout ID" />
              </div>
            </div>
            <button
              ref={closeRef}
              type="button"
              onClick={onClose}
              className="shrink-0 rounded-xl border border-[#3A4852] bg-[#1C2227]/80 px-3.5 py-2 text-[12px] font-semibold text-[#E7EBEE] transition hover:border-[#5C6B76] hover:bg-[#273037]"
            >
              Close
            </button>
          </div>
        </header>

        <div className="min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden px-4 py-5 scrollbar-thin scrollbar-track-[#13181B] scrollbar-thumb-[#3A4852] sm:px-6">
          <div className="min-w-0 space-y-5">
            <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="min-w-0 overflow-hidden rounded-xl border border-[#2C363E] bg-gradient-to-b from-[#1C2227] to-[#161E25] p-4">
                <p className="text-[10px] font-bold uppercase tracking-wider text-[#617889]">
                  Payout (NGN)
                </p>
                <p
                  className="mt-2 min-w-0 max-w-full font-sora text-lg font-bold tabular-nums leading-snug tracking-tight text-white   [overflow-wrap:anywhere]"
                  title={ngn != null ? `₦${formatAmount(ngn)}` : undefined}
                >
                  {ngn != null ? `₦${formatAmount(ngn)}` : "—"}
                </p>
              </div>
              <div className="min-w-0 overflow-hidden rounded-xl border border-[#2C363E]/80 bg-[#161E25]/60 p-4">
                <p className="text-[10px] font-bold uppercase tracking-wider text-[#617889]">
                  USD value
                </p>
                <p className="mt-2 min-w-0 max-w-full font-sora text-lg font-semibold tabular-nums leading-snug text-[#BEE1FB] [overflow-wrap:anywhere]">
                  {usd != null ? `$${formatAmount(usd)}` : "—"}
                </p>
              </div>
              <div className="min-w-0 overflow-hidden rounded-xl border border-[#2C363E]/80 bg-[#161E25]/60 p-4">
                <p className="text-[10px] font-bold uppercase tracking-wider text-[#617889]">
                  Native amount
                </p>
                <p className="mt-2 min-w-0 max-w-full font-sora text-lg font-semibold tabular-nums leading-snug text-[#E7EBEE] [overflow-wrap:anywhere]">
                  {nativeAmt != null ? (
                    <>
                      {formatAmount(nativeAmt)}{" "}
                      <span className="text-[12px] font-medium text-[#869AA9]">
                        {assetName}
                      </span>
                    </>
                  ) : (
                    "—"
                  )}
                </p>
              </div>
            </div>

            <div className="rounded-2xl border border-[#2C363E] bg-gradient-to-br from-[#1a2430]/90 via-[#151c24] to-[#13181B] p-4 sm:p-5">
              <div className="flex items-start gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[#3A4852]/80 bg-[#0D1114]/80 text-[#4FB0FF]">
                  <HiOutlineBuildingLibrary className="h-5 w-5" aria-hidden />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-[#617889]">
                    Recipient bank
                  </p>
                  <p className="mt-1 text-[17px] font-bold leading-snug text-white sm:text-[18px]">
                    {bankDisplay}
                  </p>
                  <p className="mt-2 text-[13px] text-[#B8C5CE]">
                    {rec ? str(rec.accountName) || "—" : "—"}
                  </p>
                  <p className="mt-1 font-mono text-[14px] font-semibold tabular-nums tracking-wide text-[#C6D7E4]">
                    {rec ? str(rec.accountNumber) || "—" : "—"}
                  </p>
                  {rec && str(rec.bankCode) ? (
                    <p className="mt-2 text-[11px] text-[#617889]">
                      Bank code{" "}
                      <span className="font-mono text-[#8FA3AF]">
                        {str(rec.bankCode)}
                      </span>
                    </p>
                  ) : null}
                </div>
              </div>
            </div>

            <SectionCard title="User">
              <KV label="Name" value={displayName} />
              {email ? <KV label="Email" value={email} /> : null}
              <KV
                label="User ID"
                value={
                  str(tx.userId) ? (
                    <div className="flex flex-col items-stretch gap-2 sm:items-end">
                      <span className="font-mono text-[12px] text-[#C6D7E4] break-all">
                        {str(tx.userId)}
                      </span>
                      <CopyChip text={str(tx.userId)} label="User ID" />
                    </div>
                  ) : (
                    "—"
                  )
                }
              />
            </SectionCard>

            <SectionCard title="References">
              <KV
                label="Reference"
                value={
                  str(tx.reference) ? (
                    <div className="flex flex-col items-stretch gap-2 sm:items-end">
                      <span className="font-mono text-[12px] text-[#C6D7E4] break-all">
                        {str(tx.reference)}
                      </span>
                      <CopyChip text={str(tx.reference)} label="Reference" />
                    </div>
                  ) : (
                    "—"
                  )
                }
              />
              <KV label="Source" value={str(tx.source) || "—"} />
              <KV
                label="Automated"
                value={
                  tx.isAutomated === true
                    ? "Yes"
                    : tx.isAutomated === false
                    ? "No"
                    : "—"
                }
              />
            </SectionCard>

            {linked ? (
              <SectionCard title="Linked transaction">
                <KV
                  label="Type"
                  value={
                    <span className="inline-flex rounded-md border border-[#3A4852] bg-[#0D1114]/80 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-[#AEBCC6]">
                      {str(linked.type) || "—"}
                    </span>
                  }
                />
                <KV
                  label="ID"
                  value={
                    str(linked.id) ? (
                      <div className="flex flex-col items-stretch gap-2 sm:items-end">
                        <span className="break-all font-mono text-[12px] text-[#C6D7E4]">
                          {str(linked.id)}
                        </span>
                        <CopyChip
                          text={str(linked.id)}
                          label="Transaction ID"
                        />
                      </div>
                    ) : (
                      "—"
                    )
                  }
                />
              </SectionCard>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPayoutDetailModal;
